import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const frequencyLabels = {
  once: 'Uma vez',
  weekly: 'Semanal',
  monthly: 'Mensal',
};

const weekdayLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

const riskClassMap = {
  healthy: 'badge-success',
  warning: 'badge-warning',
  risk: 'badge-danger',
};

const directionClassMap = {
  inflow: 'badge-success',
  outflow: 'badge-warning',
};

function createDefaultForm() {
  return {
    description: '',
    direction: 'outflow',
    amount: '',
    frequency: 'monthly',
    due_date: '',
    due_day_of_week: '1',
    due_day_of_month: '5',
    start_date: '',
    end_date: '',
    category: '',
    notes: '',
  };
}

function formatDate(value) {
  return value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : 'Nao informado';
}

const PersonalFinanceCashflowPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cashflow, setCashflow] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [form, setForm] = useState(createDefaultForm());

  const loadData = async () => {
    try {
      setLoading(true);
      const [cashflowResponse, commitmentsResponse] = await Promise.all([
        axios.get('/api/personal-finance/cashflow', { params: { horizon_days: 30 } }),
        axios.get('/api/personal-finance/commitments'),
      ]);

      setCashflow(cashflowResponse.data || null);
      setCommitments(Array.isArray(commitmentsResponse.data) ? commitmentsResponse.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar caixa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveCommitment = async () => {
    try {
      if (!form.description.trim()) {
        toast.error('Descricao e obrigatoria');
        return;
      }
      if (!form.amount) {
        toast.error('Valor e obrigatorio');
        return;
      }

      setSaving(true);
      await axios.post('/api/personal-finance/commitments', {
        description: form.description.trim(),
        direction: form.direction,
        amount: Number(form.amount || 0),
        frequency: form.frequency,
        due_date: form.frequency === 'once' ? form.due_date || null : null,
        due_day_of_week: form.frequency === 'weekly' ? Number(form.due_day_of_week || 0) : null,
        due_day_of_month: form.frequency === 'monthly' ? Number(form.due_day_of_month || 1) : null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        category: form.category || null,
        notes: form.notes || null,
      });
      toast.success('Compromisso criado');
      setForm(createDefaultForm());
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar compromisso');
    } finally {
      setSaving(false);
    }
  };

  const summary = cashflow?.summary || {};
  const timeline = Array.isArray(cashflow?.timeline) ? cashflow.timeline : [];
  const upcomingItems = Array.isArray(cashflow?.upcoming_items) ? cashflow.upcoming_items : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Posso gastar hoje</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : currency.format(Number(summary.safe_to_spend_today || 0))}
            </p>
            <p className="text-sm text-slate-500">Sem romper o buffer futuro atual</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Saldo base do forecast</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : currency.format(Number(summary.forecast_start_balance || 0))}
            </p>
            <p className="text-sm text-slate-500">Liquidez inicial usada no horizonte</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Menor saldo projetado</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : currency.format(Number(summary.lowest_projected_balance || 0))}
            </p>
            <p className="text-sm text-slate-500">{summary.lowest_projected_balance_date ? formatDate(summary.lowest_projected_balance_date) : 'Sem data critica'}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Status do caixa</p>
            <div>
              <span className={riskClassMap[summary.risk_status] || 'badge-info'}>
                {summary.risk_status || 'unknown'}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Buffer configurado: {loading ? '...' : currency.format(Number(summary.monthly_safety_buffer || 0))}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Forecast</p>
                  <h2 className="text-xl font-semibold text-slate-900">Caixa dos proximos 30 dias</h2>
                </div>
                <button className="btn-secondary" onClick={loadData} disabled={loading}>
                  Atualizar
                </button>
              </div>

              <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
                <p className="text-sm text-emerald-900">
                  O forecast deriva de saldo disponivel, renda principal, compromissos e faturas projetadas. Ele nao usa DRE nem fechamento contabil.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
                    <p className="text-sm text-emerald-800">Entradas</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{currency.format(Number(summary.total_inflows || 0))}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
                    <p className="text-sm text-emerald-800">Saidas</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{currency.format(Number(summary.total_outflows || 0))}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
                    <p className="text-sm text-emerald-800">Saldo final</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{currency.format(Number(summary.projected_ending_balance || 0))}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.95fr_1.05fr]">
                <div className="rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Proximos movimentos</p>
                      <p className="mt-1 text-sm text-slate-500">Os eventos mais proximos que vao mexer no caixa.</p>
                    </div>
                    <span className="badge-info">{upcomingItems.length}</span>
                  </div>
                  <div className="mt-4 space-y-3">
                    {upcomingItems.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                        Nenhum item previsto ainda. Configure renda e compromissos para alimentar o forecast.
                      </div>
                    )}
                    {upcomingItems.slice(0, 6).map((item, index) => (
                      <div key={`${item.date}-${item.description}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{item.description}</p>
                            <p className="mt-1 text-sm text-slate-500">
                              {formatDate(item.date)} · {item.category || item.source_type}
                            </p>
                          </div>
                          <span className={directionClassMap[item.direction] || 'badge-info'}>
                            {item.direction}
                          </span>
                        </div>
                        <p className="mt-3 text-lg font-semibold text-slate-900">{currency.format(Number(item.amount || 0))}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Timeline diaria</p>
                      <p className="mt-1 text-sm text-slate-500">Acompanhe onde o saldo encurta ou respira.</p>
                    </div>
                    <span className="badge-info">{timeline.length} dias</span>
                  </div>
                  <div className="mt-4 max-h-[560px] space-y-3 overflow-y-auto pr-1">
                    {timeline.map((day) => (
                      <div key={day.date} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{formatDate(day.date)}</p>
                            <p className="mt-1 text-sm text-slate-500">
                              Abertura {currency.format(Number(day.opening_balance || 0))} · Fechamento {currency.format(Number(day.closing_balance || 0))}
                            </p>
                          </div>
                          <span className={riskClassMap[day.risk_status] || 'badge-info'}>{day.risk_status}</span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div className="rounded-2xl border border-slate-200 bg-white p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Entradas</p>
                            <p className="mt-2 text-base font-semibold text-slate-900">{currency.format(Number(day.inflows || 0))}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-white p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Saidas</p>
                            <p className="mt-2 text-base font-semibold text-slate-900">{currency.format(Number(day.outflows || 0))}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-white p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Gap vs Buffer</p>
                            <p className="mt-2 text-base font-semibold text-slate-900">{currency.format(Number(day.buffer_gap || 0))}</p>
                          </div>
                        </div>
                        {Array.isArray(day.items) && day.items.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {day.items.slice(0, 4).map((item, index) => (
                              <div key={`${day.date}-${item.description}-${index}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm">
                                <span className="text-slate-600">{item.description}</span>
                                <span className="font-medium text-slate-900">{currency.format(Number(item.amount || 0))}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Novo compromisso</p>
                <h2 className="text-xl font-semibold text-slate-900">Alimentar o forecast</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Descricao</label>
                  <input className="form-input" value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Aluguel, academia, salario, escola..." />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Direcao</label>
                    <select className="form-select" value={form.direction} onChange={(event) => setForm((current) => ({ ...current, direction: event.target.value }))}>
                      <option value="outflow">Saida</option>
                      <option value="inflow">Entrada</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Valor</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={form.amount} onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Frequencia</label>
                    <select className="form-select" value={form.frequency} onChange={(event) => setForm((current) => ({ ...current, frequency: event.target.value }))}>
                      <option value="monthly">Mensal</option>
                      <option value="weekly">Semanal</option>
                      <option value="once">Uma vez</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Categoria</label>
                    <input className="form-input" value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))} placeholder="Moradia, renda, servicos..." />
                  </div>
                </div>

                {form.frequency === 'monthly' && (
                  <div>
                    <label className="form-label">Dia do mes</label>
                    <input className="form-input" type="number" min="1" max="31" value={form.due_day_of_month} onChange={(event) => setForm((current) => ({ ...current, due_day_of_month: event.target.value }))} />
                  </div>
                )}

                {form.frequency === 'weekly' && (
                  <div>
                    <label className="form-label">Dia da semana</label>
                    <select className="form-select" value={form.due_day_of_week} onChange={(event) => setForm((current) => ({ ...current, due_day_of_week: event.target.value }))}>
                      {weekdayLabels.map((label, index) => (
                        <option key={label} value={String(index)}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {form.frequency === 'once' && (
                  <div>
                    <label className="form-label">Data</label>
                    <input className="form-input" type="date" value={form.due_date} onChange={(event) => setForm((current) => ({ ...current, due_date: event.target.value }))} />
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Inicio</label>
                    <input className="form-input" type="date" value={form.start_date} onChange={(event) => setForm((current) => ({ ...current, start_date: event.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Fim</label>
                    <input className="form-input" type="date" value={form.end_date} onChange={(event) => setForm((current) => ({ ...current, end_date: event.target.value }))} />
                  </div>
                </div>

                <div>
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea min-h-[100px]" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Observacoes para a revisao semanal ou contexto do compromisso." />
                </div>
              </div>

              <button className="btn-primary w-full" onClick={saveCommitment} disabled={saving}>
                {saving ? 'Salvando...' : 'Criar compromisso'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Agenda viva</p>
                  <h2 className="text-xl font-semibold text-slate-900">Compromissos mapeados</h2>
                </div>
                <span className="badge-info">{commitments.length}</span>
              </div>

              <div className="space-y-3">
                {!loading && commitments.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhum compromisso ativo. Sem isso, o forecast depende so de renda e faturas.
                  </div>
                )}
                {commitments.map((commitment) => (
                  <div key={commitment.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{commitment.description}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {frequencyLabels[commitment.frequency] || commitment.frequency} · proxima ocorrencia {formatDate(commitment.next_occurrence_date)}
                        </p>
                      </div>
                      <span className={directionClassMap[commitment.direction] || 'badge-info'}>{commitment.direction}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 bg-white p-3">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Valor</p>
                        <p className="mt-2 text-base font-semibold text-slate-900">{currency.format(Number(commitment.amount || 0))}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-3">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Impacto mensal</p>
                        <p className="mt-2 text-base font-semibold text-slate-900">{currency.format(Number(commitment.estimated_monthly_impact || 0))}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-500">{commitment.notes || commitment.category || 'Sem notas adicionais.'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceCashflowPage;
